import { Theme, css } from '@emotion/react';

import { ButtonHTMLAttributes, ReactNode } from 'react';

import { FontSizes, Radius } from 'chatie-design-system';

import { IMarketItemProfileBtnResource } from '../../model';
import { CSSProps, OnClickProps } from '../../utils/misc';

type ButtonType = 'fill' | 'border' | 'text';

const buttonCSS =
  (
    type: ButtonType,
    color?: string,
    backgroundColor?: string,
    isDisabled = false
  ) =>
  (theme: Theme) =>
    css`
      width: 100%;
      border-radius: ${Radius.medium}px;
      font-size: ${FontSizes.small}px;
      font-weight: 600;

      ${type === 'fill' &&
      css`
        height: 48px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);

        ${isDisabled
          ? css`
              background: ${theme.var.gray200};
              color: ${theme.var.gray400};
            `
          : css`
              background: ${backgroundColor ?? theme.colors.main500};
              color: ${color ?? theme.true.white};

              &:active {
                background-image: linear-gradient(rgb(0, 0, 0, 0.1) 0 0);
              }
            `}
      `}

      ${type === 'border' &&
      css`
        height: 48px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
        border-width: 1px;
        border-style: solid;
        border-radius: ${Radius.medium}px;
        background-color: ${theme.var.white};

        ${isDisabled
          ? css`
              border-color: ${theme.var.gray200};
              color: ${theme.var.gray400};
            `
          : css`
              border-color: ${backgroundColor ?? theme.colors.main500};
              color: ${backgroundColor ?? theme.colors.main500};

              &:active {
                background-image: linear-gradient(rgb(0, 0, 0, 0.1) 0 0);
              }
            `};
      `}

      ${type === 'text' &&
      css`
        height: 24px;
        color: ${color ?? theme.var.gray500};
      `}
    `;

export type ButtonProps = {
  type?: ButtonType;
  htmlType?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  text?: string;
  color?: string;
  backgroundColor?: string;
  disabled?: boolean;
  children?: ReactNode;
  className?: string;
};

const Button = ({
  className,
  type = 'fill',
  htmlType,
  text,
  color,
  backgroundColor,
  disabled = false,
  children,
  onClick,
}: ButtonProps & OnClickProps & CSSProps) => {
  return (
    <button
      className={className}
      type={htmlType}
      css={buttonCSS(type, color, backgroundColor, disabled)}
      disabled={disabled}
      onClick={onClick}
    >
      {children || text}
    </button>
  );
};

export default Button;

type DecoratedButtonProps = {
  decoration?: IMarketItemProfileBtnResource;
};

export const DecoratedButton = ({
  className,
  decoration,
  color,
  backgroundColor,
  children,
  ...props
}: DecoratedButtonProps & ButtonProps & OnClickProps & CSSProps) => {
  const _color = decoration?.object?.textColor ?? color;
  const _backgroundColor =
    decoration?.object?.backgroundColor ?? backgroundColor;

  return (
    <Button
      className={className}
      {...props}
      color={_color}
      backgroundColor={_backgroundColor}
    >
      {children}
    </Button>
  );
};
